import React from 'react';
import { Stack, Text } from '@mantine/core';
import { AfaBadge } from '../AfaBadge/AfaBadge';
import AfaButton from '../AfaButton/AfaButton';

export interface AfaFieldDisplayProps {
  /** Etiqueta del campo (Label) */
  label: string;
  
  /** Valor del campo (puede ser texto, badge o botón) */
  value?: React.ReactNode;
  
  /** Configuración para mostrar un Badge */
  badge?: {
    color: 'yellow' | 'orange' | 'lilac' | 'red' | 'blue' | 'green';
    variant?: 'filled' | 'light';
    text: string;
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  };
  
  /** Configuración para mostrar un Botón */
  button?: {
    text: string;
    leftIcon?: React.FunctionComponent<any>;
    rightIcon?: React.FunctionComponent<any>;
    onClick?: () => void;
    id?: string;
  };
  
  /** Contenido adicional que se mostrará debajo del valor/badge/botón */
  children?: React.ReactNode;
  
  /** Alineación del contenido */
  align?: 'flex-start' | 'center' | 'flex-end';
  
  /** Espaciado entre label y valor */
  gap?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number;
  
  /** Estilos CSS personalizados */
  style?: React.CSSProperties;
}

/**
 * AfaFieldDisplay - Componente para mostrar datos en formato read-only
 * 
 * Componente flexible que permite mostrar un label con su valor, badge o botón.
 * Ideal para formularios en modo lectura, detalles de registros, etc.
 * 
 * @example
 * // Uso con texto simple
 * <AfaFieldDisplay 
 *   label="Nombre" 
 *   value="Juan Pérez" 
 * />
 * 
 * @example
 * // Uso con Badge
 * <AfaFieldDisplay 
 *   label="Estado" 
 *   badge={{ color: 'green', variant: 'light', text: 'Activo' }}
 * />
 * 
 * @example
 * // Uso con Botón
 * <AfaFieldDisplay 
 *   label="Acción" 
 *   button={{ 
 *     text: 'Ver detalles', 
 *     leftIcon: IconEye,
 *     onClick: () => console.log('Clicked')
 *   }}
 * />
 */
export const AfaFieldDisplay = ({
  label,
  value,
  badge,
  button,
  children,
  align = 'flex-start',
  gap = 6,
  style,
}: AfaFieldDisplayProps) => {
  
  // Renderizar el valor según el tipo
  const renderValue = () => {
    if (badge) {
      return (
        <AfaBadge 
          color={badge.color}
          {...(badge.variant && { variant: badge.variant })}
          {...(badge.size && { size: badge.size })}
        >
          {badge.text}
        </AfaBadge>
      );
    }
    
    if (button) {
      return (
        <AfaButton
          variant="light"
          size="sm"
          {...(button.id && { id: button.id })}
          {...(button.leftIcon && { leftIcon: button.leftIcon })}
          {...(button.rightIcon && { rightIcon: button.rightIcon })}
          {...(button.onClick && { onClick: button.onClick })}
        >
          {button.text}
        </AfaButton>
      );
    }
    
    return (
      <Text size="md" fw={400}>
        {value != null && value != "" ? value : '-'}
      </Text>
    );
  };

  return (
    <Stack gap={gap} align={align} style={{ width: '100%', ...style }}>
      <Text size="sm" fw={600}>
        {label}
      </Text>
      {(value || badge || button) && renderValue()}
      {children && (
        <div style={{ width: '100%' }}>
          {children}
        </div>
      )}
    </Stack>
  );
};
